import React, { useState, useEffect } from "react";
import { brandsParams } from "../../interface/DesignerInterface";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Skeleton from "react-loading-skeleton";
import {
  capitalizeFirstLetter,
  capitalizeFirstLetterRemainingAsItIs,
} from "../../utils/commonUtils";

type Props = {
  brands: string[];
  setBrands: (value: string[]) => void;
  closeBrandsModal: () => void;
  brandsList: { [key: string]: brandsParams[] };
};

function getParentHandle(handle: string): string | null {
  if (handle && handle.includes("/")) {
    return handle.split("/")[0];
  }
  return null;
}

function getParentHandlesMap(brandsList: { [key: string]: brandsParams[] }) {
  // Map: parentHandle -> { handles: string[], count: number }
  const parentMap: { [parent: string]: { handles: string[]; count: number } } =
    {};
  Object.values(brandsList).forEach(arr => {
    arr.forEach(item => {
      const parent = getParentHandle(item.handle || "");
      if (parent) {
        if (!parentMap[parent]) parentMap[parent] = { handles: [], count: 0 };
        parentMap[parent].handles.push(item.handle!);
        parentMap[parent].count += Number(item.count || 0);
      }
    });
  });
  return parentMap;
}

const BrandsExpandView: React.FC<Props> = ({
  brands,
  setBrands,
  closeBrandsModal,
  brandsList,
}) => {
  const [columns, setColumns] = useState<JSX.Element[][]>([]);
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [modalBrands, setModalBrands] = useState<string[]>(brands);
  const [loading, setLoading] = useState<boolean>(true);
  const [parentHandlesMap, setParentHandlesMap] = useState<{
    [parent: string]: { handles: string[]; count: number };
  }>({});

  const alphabet = "#ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  useEffect(() => {
    setParentHandlesMap(getParentHandlesMap(brandsList));
  }, [brandsList]);

  const handleBrandSelection = (brandHandle: string) => {
    setModalBrands(prev => {
      if (prev.includes(brandHandle)) {
        return prev.filter(item => item !== brandHandle);
      } else {
        return [...prev, brandHandle];
      }
    });
  };

  const handleSelectAllForLetter = (letter: string) => {
    const brandsUnderLetter =
      brandsList[letter]?.map(brand => brand.handle) || [];
    setModalBrands(prev => {
      const allSelected = brandsUnderLetter.every(handle =>
        prev.includes(handle)
      );

      if (allSelected) {
        // If all are selected, deselect them
        return prev.filter(item => !brandsUnderLetter.includes(item));
      } else {
        // Otherwise, add the ones that are not already selected
        return [
          ...prev,
          ...brandsUnderLetter.filter(handle => !prev.includes(handle)),
        ];
      }
    });
  };

  const handleParentSelection = (parent: string) => {
    const handles = parentHandlesMap[parent]?.handles || [];
    setModalBrands(prev => {
      const allSelected = handles.every(h => prev.includes(h));
      if (allSelected) {
        // Deselect all
        return prev.filter(item => !handles.includes(item));
      } else {
        // Select all
        return [...prev, ...handles.filter(h => !prev.includes(h))];
      }
    });
  };

  const organizeIntoColumns = (filterKey: string | null): JSX.Element[][] => {
    setLoading(true);
    const columnData: JSX.Element[][] = [];
    let currentColumn: JSX.Element[] = [];
    let count = 0;

    const filteredData = filterKey
      ? { [filterKey]: brandsList[filterKey] }
      : brandsList;

    Object.entries(filteredData).forEach(([key, value]) => {
      // Track which parent handles have been rendered in this column
      const renderedParents: Set<string> = new Set();

      currentColumn.push(
        <div
          key={key}
          className="font-medium text-sm ml-2 text-[#B18873] my-[2px] flex gap-2"
        >
          <input
            type="checkbox"
            style={{ accentColor: "black" }}
            checked={brandsList[key]?.every(item =>
              modalBrands.includes(item.handle!)
            )}
            onChange={() => handleSelectAllForLetter(key)}
          />
          {key}
        </div>
      );
      count++;

      if (count === 13) {
        columnData.push(currentColumn);
        currentColumn = [];
        count = 0;
      }

      value.forEach((item, index: number) => {
        const parent = getParentHandle(item.handle || "");
        // If this is the first brand for this parent in this column, render the parent select row
        if (parent && !renderedParents.has(parent)) {
          renderedParents.add(parent);
          currentColumn.push(
            <div
              key={`parent-${parent}-${key}-${index}`}
              className="flex items-center gap-2 ml-2 mb-1"
            >
              <input
                type="checkbox"
                style={{ accentColor: "black" }}
                checked={(parentHandlesMap[parent]?.handles || []).every(h =>
                  modalBrands.includes(h)
                )}
                onChange={() => handleParentSelection(parent)}
              />
              <span className="font-poppins text-xs">{parent}</span>
              <span className="ml-1 text-xs text-gray-500">
                ({parentHandlesMap[parent]?.count || 0})
              </span>
            </div>
          );
          count++;
          if (count === 13) {
            columnData.push(currentColumn);
            currentColumn = [];
            count = 0;
          }
        }

        currentColumn.push(
          <div
            key={`${key}-${index}`}
            className={`flex w-[263px] mb-1 px-2 ${modalBrands.includes(item.handle!) ? "bg-[#D3C0B6] rounded" : ""}`}
          >
            <div
              className="flex space-x-2 cursor-pointer mr-2"
              onClick={() => handleBrandSelection(item.handle!)}
            >
              <input
                type="checkbox"
                style={{ accentColor: "black" }}
                checked={modalBrands.includes(item.handle!)}
                readOnly={true}
              />
              <div>
                <Tippy
                  content={capitalizeFirstLetterRemainingAsItIs(
                    item?.brandName
                  )}
                  appendTo="parent"
                  popperOptions={{
                    modifiers: [
                      {
                        name: "preventOverflow",
                        options: {
                          boundary: "viewport",
                        },
                      },
                      {
                        name: "hide",
                        enabled: true,
                        options: {
                          padding: 0,
                        },
                      },
                    ],
                  }}
                >
                  <div className="font-poppins text-xs my-1">
                    {item?.brandName && item?.brandName.length > 15
                      ? capitalizeFirstLetterRemainingAsItIs(
                          item?.brandName
                        ).substring(0, 16) + ".."
                      : capitalizeFirstLetterRemainingAsItIs(item?.brandName)}
                  </div>
                </Tippy>
              </div>
            </div>
            <div className=" mt-1 text-xs">{`(${item.count})`}</div>
          </div>
        );
        count++;

        if (count === 13) {
          columnData.push(currentColumn);
          currentColumn = [];
          count = 0;
        }
      });
    });

    if (currentColumn.length > 0) {
      columnData.push(currentColumn);
    }
    setLoading(false);
    return columnData;
  };

  useEffect(() => {
    const columnsData = organizeIntoColumns(selectedLetter);
    setColumns(columnsData);
  }, [selectedLetter, modalBrands, parentHandlesMap]);

  const handleAlphabetClick = (letter: string) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null);
    } else {
      setSelectedLetter(letter);
    }
  };

  const handleClearAll = () => {
    setModalBrands([]);
  };

  const handleApply = () => {
    setBrands(modalBrands);
    closeBrandsModal();
  };

  return (
    <div className="relative flex flex-col bg-gray-100 h-[460px] overflow-x-hidden">
      <div className="flex">
        {alphabet.map(letter => {
          const isDisabled = !brandsList[letter];
          return (
            <button
              key={letter}
              onClick={() => !isDisabled && handleAlphabetClick(letter)}
              className={`px-[6px] mx-1 text-sm text-black font-poppins ${
                isDisabled
                  ? "text-gray-400"
                  : selectedLetter === letter
                    ? "bg-[#D3C0B6] rounded-[4px]"
                    : "cursor-pointer"
              }`}
              disabled={isDisabled}
            >
              {letter}
            </button>
          );
        })}
      </div>

      <div className="h-0.5 w-[99%] bg-black mt-1 ml-1"></div>

      {columns.length === 0 ? (
        loading ? (
          <div className="flex space-x-4 m-auto w-[840px]">
            {Array.from({ length: 3 }).map((_, index) => {
              return (
                <div className="ml-2" key={index}>
                  {Array.from({ length: 13 }).map((_, index) => {
                    return (
                      <Skeleton
                        key={index}
                        width={263}
                        height={18}
                        className="mb-2"
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <p className="text-center text-md font-semibold mt-4">
              No Brands Available
            </p>
          </div>
        )
      ) : (
        <div className="flex bg-gray-100 overflow-x-auto w-[810px] h-full">
          {columns.map((column, colIndex) => (
            <div key={colIndex} className="flex flex-col p-4">
              {column}
            </div>
          ))}
        </div>
      )}
      <div
        className={`w-[97%] flex justify-between mx-auto mt-1 ${columns.length === 0 ? "hidden" : "block"}`}
      >
        <button className="font-semibold" onClick={handleClearAll}>
          Clear All
        </button>
        <button
          className="px-4 py-1 rounded-full bg-[#D3C0B6] font-semibold"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default BrandsExpandView;
